import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  doc,
  updateDoc,
  query,
  collection,
  where,
  getDocs,
  limit,
  onSnapshot,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { db, storage } from '../lib/firebase';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/ui/Navbar';

import {
  User,
  Save,
  MapPin,
  Fingerprint,
  Calendar,
  Briefcase,
  CheckCircle2,
  Lock,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  XCircle,
  UserCircle,
  Camera,
  Star,
  Phone,
} from 'lucide-react';
import { toast } from 'sonner';

const Profile = () => {
  const navigate = useNavigate();
  const { user, userProfile, authLoading } = useAuth();

  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const [errors, setErrors] = useState({
    cpfCnpj: false,
    phone: false,
    phoneFormat: false,
  });

  const [validating, setValidating] = useState({
    cpfCnpj: false,
    cep: false,
    phone: false,
  });

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPass, setShowNewPass] = useState(false);

  const [techStats, setTechStats] = useState({ rating: 0, count: 0 });

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    cpfCnpj: '',
  });

  const [addressData, setAddressData] = useState({
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
  });

  const isTech = userProfile?.userType?.toLowerCase() === 'technician';

  const passwordRules = {
    length: newPassword.length >= 8,
    upper: /[A-Z]/.test(newPassword),
    lower: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
  };

  const isPasswordValid = Object.values(passwordRules).every(Boolean);

  const validateCPF = (c) => {
    c = c.replace(/\D/g, '');
    if (c.length !== 11 || /^(\d)\1{10}$/.test(c)) return false;

    let s = 0;
    for (let i = 0; i < 9; i++) s += parseInt(c.charAt(i), 10) * (10 - i);

    let r = 11 - (s % 11);
    if (r >= 10) r = 0;
    if (r !== parseInt(c.charAt(9), 10)) return false;

    s = 0;
    for (let i = 0; i < 10; i++) s += parseInt(c.charAt(i), 10) * (11 - i);

    r = 11 - (s % 11);
    if (r >= 10) r = 0;

    return r === parseInt(c.charAt(10), 10);
  };

  const validateCNPJ = (c) => {
    c = c.replace(/\D/g, '');
    if (c.length !== 14 || /^(\d)\1{13}$/.test(c)) return false;

    let b = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let s = 0;
    for (let i = 0; i < 12; i++) s += parseInt(c.charAt(i), 10) * b[i];

    let r = s % 11;
    if (parseInt(c.charAt(12), 10) !== (r < 2 ? 0 : 11 - r)) return false;

    b = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    s = 0;
    for (let i = 0; i < 13; i++) s += parseInt(c.charAt(i), 10) * b[i];

    r = s % 11;
    return parseInt(c.charAt(13), 10) === (r < 2 ? 0 : 11 - r);
  };

  const validatePhone = (value) => {
    const clean = value.replace(/\D/g, '');
    return /^\d{10,11}$/.test(clean);
  };

  const maskPhone = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);

    if (digits.length <= 10) {
      return digits
        .replace(/^(\d{0,2})(\d{0,4})(\d{0,4})$/, (_, ddd, part1, part2) => {
          let result = '';
          if (ddd) result = `(${ddd}`;
          if (ddd.length === 2) result += ')';
          if (part1) result += ` ${part1}`;
          if (part2) result += `-${part2}`;
          return result.trim();
        });
    }

    return digits
      .replace(/^(\d{0,2})(\d{0,5})(\d{0,4})$/, (_, ddd, part1, part2) => {
        let result = '';
        if (ddd) result = `(${ddd}`;
        if (ddd.length === 2) result += ')';
        if (part1) result += ` ${part1}`;
        if (part2) result += `-${part2}`;
        return result.trim();
      });
  };

  const maskCpfCnpj = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 14);

    if (digits.length <= 11) {
      return digits
        .replace(
          /^(\d{0,3})(\d{0,3})(\d{0,3})(\d{0,2})$/,
          (_, a, b, c, d) => {
            let result = a;
            if (b) result += `.${b}`;
            if (c) result += `.${c}`;
            if (d) result += `-${d}`;
            return result;
          }
        );
    }

    return digits
      .replace(
        /^(\d{0,2})(\d{0,3})(\d{0,3})(\d{0,4})(\d{0,2})$/,
        (_, a, b, c, d, e) => {
          let result = a;
          if (b) result += `.${b}`;
          if (c) result += `.${c}`;
          if (d) result += `/${d}`;
          if (e) result += `-${e}`;
          return result;
        }
      );
  };

  const maskCep = (value) =>
    value.replace(/\D/g, '').slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2');

  useEffect(() => {
    if (isTech && user?.uid) {
      const q = query(collection(db, 'orders'), where('technicianId', '==', user.uid));

      const unsub = onSnapshot(q, (snap) => {
        const evaluations = snap.docs.map((d) => d.data()).filter((o) => o.rating > 0);
        const total = evaluations.reduce((acc, curr) => acc + curr.rating, 0);
        const avg = evaluations.length > 0 ? total / evaluations.length : 0;

        setTechStats({ rating: avg, count: evaluations.length });
      });

      return () => unsub();
    }
  }, [isTech, user?.uid]);

  useEffect(() => {
    if (userProfile) {
      setFormData({
        name: userProfile.name || '',
        phone: userProfile.phone ? maskPhone(userProfile.phone) : '',
        cpfCnpj: userProfile.cpfCnpj ? maskCpfCnpj(userProfile.cpfCnpj) : '',
      });

      setAddressData({
        cep: userProfile.addressDetails?.cep
          ? maskCep(userProfile.addressDetails.cep)
          : '',
        street: userProfile.addressDetails?.street || '',
        number: userProfile.addressDetails?.number || '',
        complement: userProfile.addressDetails?.complement || '',
        neighborhood: userProfile.addressDetails?.neighborhood || '',
        city: userProfile.addressDetails?.city || '',
        state: userProfile.addressDetails?.state || '',
      });

      setPhotoPreview(userProfile.photoURL || null);
    }
  }, [userProfile]);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato inválido! Use apenas PNG, JPG ou JPEG.');
      return;
    }

    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleCpfCnpjBlur = async () => {
    const clean = formData.cpfCnpj.replace(/\D/g, '');

    if (!clean || clean === userProfile?.cpfCnpj) {
      setErrors((prev) => ({ ...prev, cpfCnpj: false }));
      return;
    }

    const isValidDocument =
      clean.length === 11 ? validateCPF(clean) : clean.length === 14 ? validateCNPJ(clean) : false;

    if (!isValidDocument) {
      setErrors((prev) => ({ ...prev, cpfCnpj: true }));
      toast.error('Documento inválido!');
      return;
    }

    setValidating((prev) => ({ ...prev, cpfCnpj: true }));

    try {
      const q = query(collection(db, 'users'), where('cpfCnpj', '==', clean), limit(1));
      const snap = await getDocs(q);

      if (!snap.empty && snap.docs[0].id !== user.uid) {
        setErrors((prev) => ({ ...prev, cpfCnpj: true }));
        toast.error('Este documento já está em uso!');
      } else {
        setErrors((prev) => ({ ...prev, cpfCnpj: false }));
      }
    } finally {
      setValidating((prev) => ({ ...prev, cpfCnpj: false }));
    }
  };

  const checkPhoneUniqueness = useCallback(
    async (phoneValue) => {
      const clean = phoneValue.replace(/\D/g, '');

      if (!clean) {
        setErrors((prev) => ({ ...prev, phone: false, phoneFormat: false }));
        return;
      }

      if (!validatePhone(clean)) {
        setErrors((prev) => ({ ...prev, phone: true, phoneFormat: true }));
        return;
      }

      if (clean === userProfile?.phone) {
        setErrors((prev) => ({ ...prev, phone: false, phoneFormat: false }));
        return;
      }

      setValidating((prev) => ({ ...prev, phone: true }));

      try {
        const q = query(collection(db, 'users'), where('phone', '==', clean), limit(1));
        const snap = await getDocs(q);

        if (!snap.empty && snap.docs[0].id !== user.uid) {
          setErrors((prev) => ({ ...prev, phone: true, phoneFormat: false }));
        } else {
          setErrors((prev) => ({ ...prev, phone: false, phoneFormat: false }));
        }
      } catch (error) {
        console.error(error);
      } finally {
        setValidating((prev) => ({ ...prev, phone: false }));
      }
    },
    [user?.uid, userProfile?.phone]
  );

  useEffect(() => {
    const phone = formData.phone;
    const clean = phone.replace(/\D/g, '');

    if (!phone) {
      setErrors((prev) => ({ ...prev, phone: false, phoneFormat: false }));
      return;
    }

    if (clean.length < 10) {
      setErrors((prev) => ({ ...prev, phone: true, phoneFormat: true }));
      return;
    }

    const timer = setTimeout(() => {
      checkPhoneUniqueness(phone);
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.phone, checkPhoneUniqueness]);

  const handleCepBlur = async () => {
    const clean = addressData.cep.replace(/\D/g, '');

    if (clean.length !== 8) return;

    setValidating((prev) => ({ ...prev, cep: true }));

    try {
      const res = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
      const data = await res.json();

      if (data.erro) {
        toast.error('CEP não encontrado.');
      } else {
        setAddressData((prev) => ({
          ...prev,
          street: data.logradouro?.toUpperCase() || '',
          neighborhood: data.bairro?.toUpperCase() || 'CENTRO',
          city: data.localidade?.toUpperCase() || '',
          state: data.uf?.toUpperCase() || '',
        }));
      }
    } catch (error) {
      toast.error('Erro ao consultar o CEP.');
    } finally {
      setValidating((prev) => ({ ...prev, cep: false }));
    }
  };

  const handleUpdateProfile = async () => {
    if (!photoPreview) {
      toast.error('Foto obrigatória!');
      return;
    }

    if (errors.cpfCnpj) {
      toast.error('Corrija o CPF/CNPJ!');
      return;
    }

    if (errors.phoneFormat) {
      toast.error('Telefone inválido!');
      return;
    }

    if (errors.phone) {
      toast.error('Este telefone já está em uso!');
      return;
    }

    if (!validatePhone(formData.phone)) {
      toast.error('Informe um telefone válido!');
      return;
    }

    setLoading(true);

    try {
      let photoURL = userProfile?.photoURL || '';

      if (photoFile) {
        const photoRef = ref(storage, `profile-photos/${user.uid}`);
        await uploadBytes(photoRef, photoFile);
        photoURL = await getDownloadURL(photoRef);
      }

      await updateDoc(doc(db, 'users', user.uid), {
        name: formData.name,
        phone: formData.phone.replace(/\D/g, ''),
        cpfCnpj: formData.cpfCnpj.replace(/\D/g, ''),
        addressDetails: {
          cep: addressData.cep.replace(/\D/g, ''),
          street: addressData.street,
          number: addressData.number,
          complement: addressData.complement,
          neighborhood: addressData.neighborhood,
          city: addressData.city,
          state: addressData.state,
        },
        photoURL,
        updatedAt: new Date().toISOString(),
      });

      toast.success('Perfil atualizado!');
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      toast.error('Erro ao salvar.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (!isPasswordValid) {
      toast.error('Senha fraca demais.');
      return;
    }

    setLoading(true);

    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);

      toast.success('Senha trocada!');
      setCurrentPassword('');
      setNewPassword('');
    } catch (error) {
      toast.error('Senha atual incorreta.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) return null;

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 text-left">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 pt-32 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-3 space-y-6">
          <div className="space-y-2">
            <Button
              onClick={() => navigate('/profile')}
              className="w-full h-14 justify-start px-6 rounded-2xl bg-blue-600 text-white font-black uppercase italic text-[10px] gap-4 shadow-xl"
            >
              <UserCircle size={18} /> Meu Perfil
            </Button>

            {isTech && (
              <Button
                onClick={() => navigate('/profile-tech')}
                variant="ghost"
                className="w-full h-14 justify-start px-6 rounded-2xl bg-white text-slate-400 font-black uppercase italic text-[10px] gap-4 border border-slate-100 shadow-sm"
              >
                <Briefcase size={18} /> Dados Profissionais
              </Button>
            )}
          </div>

          <Card className="rounded-[2rem] border-none shadow-xl bg-slate-900 text-white p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-white/10 pb-3 text-left">
              <div className="flex items-center gap-2">
                <Fingerprint className="w-4 h-4 text-blue-400" />
                <span className="text-[9px] font-black uppercase">Acesso</span>
              </div>
              <Badge className="bg-blue-600 border-none font-black italic uppercase text-[8px]">
                {isTech ? 'Profissional' : 'Cliente'}
              </Badge>
            </div>

            {isTech && (
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-400" />
                  <span className="text-[9px] font-black uppercase">Reputação</span>
                </div>
                <div className="text-right">
                  <span className="text-[10px] font-black text-yellow-400">
                    {techStats.rating.toFixed(1)}
                  </span>
                  <span className="text-[7px] block font-bold text-slate-500">
                    {techStats.count} avaliações
                  </span>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between text-left">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="text-[9px] font-black uppercase">Membro</span>
              </div>
              <span className="text-[10px] font-black italic">
                {userProfile?.createdAt
                  ? new Date(userProfile.createdAt).toLocaleDateString()
                  : '---'}
              </span>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-9 space-y-8 text-left">
          <Card className="rounded-[2.5rem] border-none shadow-2xl bg-white border-t-8 border-blue-600 p-8 md:p-10">
            <CardContent className="p-0 space-y-10 text-left">
              <div className="flex items-center gap-6 bg-slate-50 p-6 rounded-[2rem] border border-slate-100 text-left">
                <div className="relative w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-lg bg-white shrink-0 group">
                  {photoPreview ? (
                    <img src={photoPreview} className="w-full h-full object-cover" alt="" />
                  ) : (
                    <User className="w-8 h-8 m-auto mt-8 text-slate-200" />
                  )}

                  <label
                    htmlFor="photo-up"
                    className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  >
                    <Camera className="text-white w-6 h-6" />
                    <input
                      id="photo-up"
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      className="hidden"
                      onChange={handlePhotoChange}
                    />
                  </label>
                </div>

                <div className="text-left flex-1">
                  <h4 className="font-black text-blue-600 uppercase italic text-sm mb-1">
                    Foto de Perfil
                  </h4>
                  <p className="text-[9px] font-bold text-slate-400 uppercase italic">
                    Formato JPG, JPEG ou PNG apenas.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-10 text-left">
                <div className="space-y-6 text-left">
                  <div className="space-y-1 text-left">
                    <Label className="text-[10px] font-black uppercase ml-2 text-slate-400 italic">
                      Nome Completo
                    </Label>
                    <Input
                      value={formData.name}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          name: e.target.value.toUpperCase(),
                        }))
                      }
                      className="h-14 rounded-2xl bg-slate-50 border-none font-bold uppercase italic shadow-inner"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-left">
                    <div className="space-y-1 text-left relative">
                      <Label
                        className={`text-[10px] font-black uppercase ml-2 italic flex items-center gap-1 ${
                          errors.phone ? 'text-red-500' : 'text-blue-600'
                        }`}
                      >
                        <Phone size={10} /> Telefone
                      </Label>

                      <Input
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            phone: maskPhone(e.target.value),
                          }))
                        }
                        className={`h-14 rounded-2xl border font-bold shadow-inner ${
                          errors.phone
                            ? 'border-red-500 bg-red-50'
                            : 'border-blue-100 bg-blue-50/30'
                        }`}
                        placeholder="(00) 00000-0000"
                      />

                      {validating.phone && (
                        <Loader2 className="absolute right-3 top-9 w-4 h-4 animate-spin text-blue-600" />
                      )}

                      {errors.phoneFormat && (
                        <p className="text-[10px] text-red-500 font-bold ml-2 mt-1">
                          Informe um telefone válido.
                        </p>
                      )}

                      {!errors.phoneFormat && errors.phone && (
                        <p className="text-[10px] text-red-500 font-bold ml-2 mt-1">
                          Este telefone já está em uso.
                        </p>
                      )}
                    </div>

                    <div className="space-y-1 text-left relative">
                      <Label
                        className={`text-[10px] font-black uppercase ml-2 italic ${
                          errors.cpfCnpj ? 'text-red-500' : 'text-slate-400'
                        }`}
                      >
                        CPF ou CNPJ
                      </Label>

                      <Input
                        value={formData.cpfCnpj}
                        onBlur={handleCpfCnpjBlur}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            cpfCnpj: maskCpfCnpj(e.target.value),
                          }))
                        }
                        className={`h-14 rounded-2xl border-2 font-bold shadow-inner ${
                          errors.cpfCnpj
                            ? 'border-red-500 bg-red-50'
                            : 'border-transparent bg-slate-50'
                        }`}
                      />

                      {validating.cpfCnpj && (
                        <Loader2 className="absolute right-3 top-9 w-4 h-4 animate-spin text-blue-600" />
                      )}
                    </div>
                  </div>

                  <div className="pt-6 border-t border-slate-100 space-y-4 text-left">
                    <h3 className="font-black text-slate-800 uppercase italic text-xs flex items-center gap-2">
                      <MapPin size={16} className="text-blue-600" /> Endereço
                    </h3>

                    <div className="grid grid-cols-12 gap-3">
                      <div className="col-span-5 relative">
                        <Label className="text-[8px] font-black text-slate-400 uppercase ml-2 mb-1">
                          CEP
                        </Label>
                        <Input
                          value={addressData.cep}
                          onBlur={handleCepBlur}
                          onChange={(e) =>
                            setAddressData((prev) => ({
                              ...prev,
                              cep: maskCep(e.target.value),
                            }))
                          }
                          className="h-14 rounded-2xl font-bold text-center shadow-inner bg-slate-50 border-none"
                        />
                        {validating.cep && (
                          <Loader2 className="absolute right-2 top-8 w-4 h-4 animate-spin text-blue-600" />
                        )}
                      </div>

                      <div className="col-span-7">
                        <Label className="text-[8px] font-black text-slate-400 uppercase ml-2 mb-1">
                          Rua / Logradouro
                        </Label>
                        <Input
                          value={addressData.street}
                          onChange={(e) =>
                            setAddressData((prev) => ({
                              ...prev,
                              street: e.target.value.toUpperCase(),
                            }))
                          }
                          className="h-14 rounded-2xl bg-slate-50 border-none font-bold px-6 shadow-inner uppercase"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-left">
                      <div className="space-y-1">
                        <Label className="text-[8px] font-black text-slate-400 uppercase ml-2 mb-1">
                          Número
                        </Label>
                        <Input
                          value={addressData.number}
                          onChange={(e) =>
                            setAddressData((prev) => ({
                              ...prev,
                              number: e.target.value.toUpperCase(),
                            }))
                          }
                          className="h-14 rounded-2xl bg-slate-50 border-none font-bold px-6 shadow-inner uppercase"
                          placeholder="Ex: 123"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-[8px] font-black text-slate-400 uppercase ml-2 mb-1">
                          Complemento
                        </Label>
                        <Input
                          value={addressData.complement}
                          onChange={(e) =>
                            setAddressData((prev) => ({
                              ...prev,
                              complement: e.target.value.toUpperCase(),
                            }))
                          }
                          className="h-14 rounded-2xl bg-slate-50 border-none font-bold px-6 shadow-inner uppercase"
                          placeholder="Apto, bloco, casa..."
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-left">
                      <div className="space-y-1">
                        <Label className="text-[8px] font-black text-blue-600 uppercase ml-2 italic">
                          Bairro (Automático)
                        </Label>
                        <Input
                          value={addressData.neighborhood}
                          readOnly
                          className="h-14 rounded-2xl bg-slate-100 border-none font-bold px-6 text-slate-500 uppercase italic cursor-not-allowed"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-[8px] font-black text-blue-600 uppercase ml-2 italic">
                          Cidade/UF (Automático)
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            value={addressData.city}
                            readOnly
                            className="h-14 rounded-2xl bg-slate-100 border-none font-bold px-6 text-slate-500 uppercase italic cursor-not-allowed flex-1"
                          />
                          <Input
                            value={addressData.state}
                            readOnly
                            className="h-14 w-14 rounded-2xl bg-slate-100 border-none font-bold text-center text-slate-500 italic cursor-not-allowed"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleUpdateProfile}
                    disabled={loading || errors.cpfCnpj || errors.phone}
                    className="w-full h-16 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-2xl uppercase italic text-xs shadow-xl active:scale-95 transition-all"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin mr-2" />
                    ) : (
                      <Save className="mr-2" />
                    )}
                    SALVAR ALTERAÇÕES
                  </Button>
                </div>

                <div className="space-y-6 p-8 bg-slate-900 rounded-[2.5rem] text-white shadow-xl h-fit text-left">
                  <div className="flex items-center gap-3 mb-2">
                    <Lock className="text-red-500" size={20} />
                    <h3 className="font-black uppercase italic text-sm">Segurança</h3>
                  </div>

                  <form onSubmit={handlePasswordChange} className="space-y-4 text-left">
                    <div className="space-y-1">
                      <Label className="text-[9px] font-black uppercase text-slate-400 ml-1 italic">
                        Senha Atual
                      </Label>
                      <Input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="h-12 bg-white/5 border-white/10 rounded-xl px-4 font-bold text-white"
                        placeholder="••••••••"
                        required
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-[9px] font-black uppercase text-slate-400 ml-1 italic">
                        Nova Senha
                      </Label>

                      <div className="relative">
                        <Input
                          type={showNewPass ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="h-12 bg-white/5 border-white/10 rounded-xl px-4 font-bold text-white"
                          placeholder="••••••••"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPass(!showNewPass)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                        >
                          {showNewPass ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-2 p-3 mt-2 bg-black/20 rounded-xl border border-white/5 text-left">
                        {Object.entries(passwordRules).map(([key, val]) => (
                          <span
                            key={key}
                            className={`text-[7px] font-black uppercase flex items-center gap-1 ${
                              val ? 'text-green-400' : 'text-slate-600'
                            }`}
                          >
                            {val ? <CheckCircle2 size={8} /> : <XCircle size={8} />}
                            {key === 'length'
                              ? '8+ Chars'
                              : key === 'upper'
                              ? 'Maiúscula'
                              : key === 'lower'
                              ? 'Minúscula'
                              : 'Número'}
                          </span>
                        ))}
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading || !isPasswordValid}
                      className="w-full h-14 bg-red-600 hover:bg-red-700 text-white font-black rounded-xl uppercase italic text-[10px] shadow-lg transition-all active:scale-95"
                    >
                      <KeyRound size={16} className="mr-2" /> ATUALIZAR ACESSO
                    </Button>
                  </form>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Profile;